import { createRootRoute, Navigate, Outlet, useLocation, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { usePortalSession } from '@/lib/api/portalSession';
import { decidePortalGuard } from '@/lib/auth/portalGuard';
import { GuardErrorScreen } from '@/lib/auth/GuardErrorScreen';
import { GuardOfflineScreen } from '@/lib/auth/GuardOfflineScreen';
import { PropertyProvider } from '@/lib/property/PropertyContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { SyncBar } from '@/lib/desk/ui/SyncBar';
import { useGuardedSignOut } from '@/lib/desk/ui/useGuardedSignOut';
import { useHostProductsGate } from '@/lib/data/featureFlags';
import { useBusinessCategory } from '@/lib/data/verification';
import { ThemeToggle } from '@/lib/theme/ThemeToggle';
import { AppShell, SidebarAction } from '@/ui/Shell';
import { ScreenLoading } from '@/ui/Loader';
import { navIconFor } from '@/ui/navIcons';
import { LogOutIcon } from '@/icons/nav';
import { PublicFrame } from '@/ui/PublicFrame';
import { useReceptionIdleSignOut } from '@/lib/auth/useReceptionIdleSignOut';
import {
  BUSINESS_NAV,
  HOST_PRODUCTS_NAV,
  RESTAURANT_NAV,
  filterNavByRole,
  isPublicBusinessPath,
} from '@/lib/nav/businessNav';

export const Route = createRootRoute({
  component: BusinessRootLayout,
});

function BusinessRootLayout() {
  const { session, isLoading: sessionLoading, signOut } = useAuth();
  const location = useLocation();
  // Decide the branch from what <Outlet/> will actually RENDER — the
  // router's current matches — not from the location, which switches to the
  // new URL while the old matches are still on screen. On sign-out from
  // /bookings the location is already /sign-in for a render or two while
  // the matches are still /bookings; rendering the public branch then put
  // BookingsList outside PropertyProvider ("useProperty must be used within
  // a PropertyProvider"). Task 14 fix round 1.
  const renderedPath = useRouterState({
    select: (state) =>
      (state.matches[state.matches.length - 1]?.pathname ?? state.location.pathname).replace(/(.)\/$/, '$1'),
  });
  const isPublic = isPublicBusinessPath(location.pathname) && isPublicBusinessPath(renderedPath);

  const portalSessionQuery = usePortalSession();

  if (sessionLoading) {
    return <ScreenLoading />;
  }

  // Any authenticated route beyond sign-in/invite requires a session; an
  // unauthenticated visit anywhere else bounces to sign-in.
  if (!session && !isPublic) {
    // Already on the way to a public route (the location switched, the old
    // matches haven't been replaced yet): wait — rendering <Navigate/> again
    // here would restart that very navigation on every render.
    if (isPublicBusinessPath(location.pathname)) {
      return <ScreenLoading />;
    }
    return <Navigate to="/sign-in" />;
  }

  // Signed-in users shouldn't sit on the sign-in screen.
  if (session && location.pathname === '/sign-in') {
    return <Navigate to="/" />;
  }

  if (isPublic) {
    return (
      <PublicFrame tone="business">
        <Outlet />
      </PublicFrame>
    );
  }

  const decision = decidePortalGuard({
    hasSession: !!session,
    sessionLoading,
    portalSessionLoading: portalSessionQuery.isLoading,
    portalSessionError: portalSessionQuery.isError,
    portalSessionFetchStatus: portalSessionQuery.fetchStatus,
    portalSessionLoaded: !!portalSessionQuery.data,
  });

  if (decision === 'loading') {
    return <ScreenLoading />;
  }
  if (decision === 'sign-in') {
    return <Navigate to="/sign-in" />;
  }
  if (decision === 'offline') {
    return <GuardOfflineScreen onSignOut={signOut} />;
  }
  if (decision === 'error') {
    // fix round 1, item 4: portal-session's isError used to be ignored
    // entirely, falling through to the full owner nav with an empty
    // membership list instead of surfacing the failure.
    return <GuardErrorScreen onRetry={() => void portalSessionQuery.refetch()} onSignOut={signOut} />;
  }

  // decision === 'ready'
  const memberships = portalSessionQuery.data?.memberships ?? [];

  return (
    <PropertyProvider memberships={memberships} loading={portalSessionQuery.isLoading} userId={session!.user.id}>
      <BusinessShell sessionLoaded={!!portalSessionQuery.data} />
    </PropertyProvider>
  );
}

function BusinessShell({ sessionLoaded }: { sessionLoaded: boolean }) {
  const { businessId, role, memberships, selectBusiness } = useProperty();
  const { supabase, session } = useAuth();
  // Follow-up F4 (decision 11): reception sessions sign out after 30 idle
  // minutes — the plain session sign-out, deliberately NOT the guarded one
  // below (an unattended desk can't answer its modal); unsynced desk events
  // stay encrypted on the device and resume after the next sign-in.
  useReceptionIdleSignOut(role, session, () => supabase.auth.signOut({ scope: 'local' }));
  // Sign-out is blocked behind the unsynced modal while the desk holds
  // events NamibiaGo hasn't confirmed (Task 14).
  const guardedSignOut = useGuardedSignOut();

  // Both the flag AND the entity must be on (20260926000009's ruling) —
  // hidden from nav until then, same as the hardcoded-false state this
  // replaces.
  const { gateOpen: hostProductsOn } = useHostProductsGate(supabase);
  const categoryQuery = useBusinessCategory(supabase, businessId);
  const isRestaurant = categoryQuery.data === 'restaurant';
  const navItems = filterNavByRole(
    [...BUSINESS_NAV, ...(isRestaurant ? RESTAURANT_NAV : []), ...(hostProductsOn ? HOST_PRODUCTS_NAV : [])],
    role,
  );
  // TASKS 110: an account that owns/staffs nothing used to land on an empty
  // reception dashboard, and owner pages spun "Loading…" forever (OwnerOnly
  // waits on a businessId that never comes). Registration is app-only, so say so.
  const noBusiness = sessionLoaded && memberships.length === 0;

  return (
    <AppShell
      tone="business"
      productName="Business"
      nav={navItems.map((item) => ({ ...item, icon: navIconFor(item.to) }))}
      sidebarTop={
        sessionLoaded && memberships.length > 0 ? (
          <label className="flex flex-col gap-xs px-xs text-caption font-semibold text-ink-2">
            Property
            <select
              value={businessId ?? ''}
              onChange={(e) => selectBusiness(e.target.value)}
              className="w-full rounded-input border border-border bg-background px-sm py-xs text-body font-semibold text-ink"
              aria-label="Select property"
            >
              {memberships.map((m) => (
                <option key={m.businessId} value={m.businessId}>
                  {m.businessName ?? m.businessId}
                </option>
              ))}
            </select>
          </label>
        ) : null
      }
      sidebarBottom={
        <div className="flex flex-col gap-[2px]">
          <ThemeToggle />
          <SidebarAction tone="business" icon={<LogOutIcon />} onClick={() => guardedSignOut.request('local')}>
            Sign out
          </SidebarAction>
          <SidebarAction tone="business" subtle onClick={() => guardedSignOut.request('global')}>
            Sign out all devices
          </SidebarAction>
        </div>
      }
      banner={
        <>
          <SyncBar />
          {guardedSignOut.modal}
        </>
      }
    >
      {noBusiness ? <NoBusinessYet /> : <Outlet />}
    </AppShell>
  );
}

function NoBusinessYet() {
  return (
    <section className="mx-auto max-w-xl rounded-card border border-border bg-surface p-xl shadow-sm" data-testid="no-business">
      <h1 className="text-screen-title font-extrabold tracking-tight text-ink">No business on this account yet</h1>
      <p className="mt-sm text-body text-ink-2">
        Businesses are registered in the NamibiaGo mobile app: sign in there with this same account, then go to
        Profile → Register Your Business. You&apos;ll add your details, upload your verification documents and sign
        the Host Agreement. Once we approve it, the business appears here.
      </p>
      <p className="mt-sm text-body text-ink-2">
        Joining a team instead? Ask the owner to invite this email address from their Team page.
      </p>
    </section>
  );
}
